import React from "react";
import { resolveUploadUrl } from "../../../utils/apiClient";
import { useCurrency } from "../../../hooks/useCurrency";
import DetailsModal from "../../../components/forms/DetailsModal";
import detailStyles from "../../../components/forms/DetailsModal.module.css";

export default function EmployeeDetailModal({
  employee,
  getJobTitleName,
  canEditPeople,
  canDeletePeople,
  canInvitePeople,
  onClose,
  onEditEmployee,
  onEditPlaceholder,
  onDeleteClick,
  onCancelInviteClick,
}) {
  const { format: formatMoney } = useCurrency();

  const onEdit = employee.kind === 'ghost'
    ? (canEditPeople ? onEditPlaceholder : null)
    : (employee.status === 'invited' ? null : (canEditPeople ? onEditEmployee : null));

  const onDelete = employee.kind === 'ghost'
    ? (canDeletePeople ? onDeleteClick : null)
    : (employee.status === 'invited'
        ? (canInvitePeople ? onCancelInviteClick : null)
        : (canDeletePeople ? onDeleteClick : null));

  return (
    <DetailsModal
      title="Employee Details"
      onClose={onClose}
      onEdit={onEdit}
      onDelete={onDelete}
      deleteLabel={employee.kind === 'ghost' ? "Delete Contractor" : (employee.status === 'invited' ? "Cancel Invitation" : "Remove Employee")}
      editLabel={employee.kind === 'ghost' ? "Edit Contractor" : "Edit Employee"}
    >
      {/* Profile Header */}
      <div className={detailStyles.detailSection}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: '3px solid rgba(255,255,255,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: '600',
            color: 'white',
            flexShrink: 0
          }}>
            {employee.avatar_url ? (
              <img
                src={resolveUploadUrl(employee.avatar_url)}
                alt={employee.name}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              employee.name.split(' ').map(n => n[0]).join('').toUpperCase()
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '600', color: 'white' }}>
              {employee.name}
            </h3>
            <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
              {employee.job_title || getJobTitleName(employee.title_id) || 'No title assigned'}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Contact Information</h4>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Email</span>
          <span className={`${detailStyles.detailValue} ${!employee.email ? detailStyles.empty : ''}`}>
            {employee.email ? (
              <a href={`mailto:${employee.email}`} style={{ color: '#6366f1', textDecoration: 'none' }}>
                {employee.email}
              </a>
            ) : '—'}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Phone</span>
          <span className={`${detailStyles.detailValue} ${!(employee.phone_number || employee.phone) ? detailStyles.empty : ''}`}>
            {employee.phone_number || employee.phone || '—'}
          </span>
        </div>
      </div>

      {/* Work Details */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Work Details</h4>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Job Title</span>
          <span className={`${detailStyles.detailValue} ${!(employee.job_title || getJobTitleName(employee.title_id)) ? detailStyles.empty : ''}`}>
            {employee.job_title || getJobTitleName(employee.title_id) || '—'}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Hourly Rate</span>
          <span className={`${detailStyles.detailValue} ${!employee.default_hourly_rate ? detailStyles.empty : ''}`}>
            {employee.default_hourly_rate ? (
              <span style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                padding: '4px 10px',
                borderRadius: '6px',
                fontWeight: '500',
                fontSize: '0.9rem'
              }}>
                {formatMoney(employee.default_hourly_rate)}/hr
              </span>
            ) : '—'}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Weekly Hours</span>
          <span className={detailStyles.detailValue}>
            <span style={{
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              padding: '4px 10px',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '0.9rem'
            }}>
              {employee.default_weekly_hours || '40'} hrs/week
            </span>
          </span>
        </div>

        {employee.kind === 'ghost' && employee.description && (
          <div className={detailStyles.detailRow}>
            <span className={detailStyles.detailLabel}>Description</span>
            <span className={detailStyles.detailValue}>
              {employee.description}
            </span>
          </div>
        )}
      </div>

      {/* Permissions */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>
          Permissions
          {employee.permissions && employee.permissions.length > 0 && (
            <span style={{
              marginLeft: '8px',
              backgroundColor: '#e5e7eb',
              color: '#374151',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: '500'
            }}>
              {employee.permissions.length}
            </span>
          )}
        </h4>

        {employee.permissions && employee.permissions.length > 0 ? (
          <div className={detailStyles.labelsList}>
            {employee.permissions.map((perm, idx) => (
              <span
                key={idx}
                className={detailStyles.labelTag}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  padding: '6px 12px',
                  borderRadius: '6px'
                }}
              >
                {perm.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        ) : (
          <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
            No permissions assigned
          </p>
        )}
      </div>
    </DetailsModal>
  );
}
